import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, Zap, Calendar, Users, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    venueCapacity: '',
    eventType: '',
    eventDate: '',
    rentalDuration: '',
    venueAccessNotes: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit request');
      }
      
      await response.json();
      
      toast({
        title: "Request Received!",
        description: "Our team will get back to you within 2 hours with a tailored quote.",
      });
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        venueCapacity: '',
        eventType: '',
        eventDate: '',
        rentalDuration: '',
        venueAccessNotes: '',
        message: ''
      });
    } catch (error) {
      toast({
        title: "Submission Failed",
        description: "Please try again or contact us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Service Area',
      details: ['London & Surrounding Areas', 'Events from 20-2,000 guests'],
      color: 'from-slate-700 to-blue-900'
    },
    {
      icon: Phone,
      title: 'Phone',
      details: [<a href="tel:02080495480" className="hover:text-white transition-colors">020 8049 5480</a>, '24/7 Emergency Line'],
      color: 'from-slate-700 to-blue-900'
    },
    {
      icon: Mail,
      title: 'Email',
      details: ['info@audioapex.co.uk', 'Response within 2 hours'],
      color: 'from-slate-700 to-blue-900'
    },
    {
      icon: Clock,
      title: 'Availability',
      details: ['Last Minute Hire Available', 'Long Term Rentals Welcome'],
      color: 'from-slate-700 to-blue-900'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-900 pt-20">
      {/* Header */}
      <section className="bg-slate-900 py-16 px-4 sm:px-6 lg:px-8 border-b border-slate-800">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="mb-3 bg-[#e2e8f0] text-slate-800 border-0 px-4 py-1.5 text-xs font-semibold">
            GET IN TOUCH
          </Badge>
          <h1 className="text-4xl font-bold text-white mb-3 heading-font">Request Your Quote</h1>
          <p className="text-base text-gray-400 max-w-2xl mx-auto">
            Last-minute hire or long-term rental? Get a tailored quote within 2 hours for events across London and surrounding areas.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 mt-6">
            <a href="tel:02080495480" className="flex items-center gap-2 text-white hover:text-[#e2e8f0] transition-colors" data-testid="contact-hero-phone">
              <div className="w-9 h-9 bg-[#e2e8f0] rounded-lg flex items-center justify-center shadow-md">
                <Phone className="w-4 h-4 text-slate-900" />
              </div>
              <span className="font-medium">020 8049 5480</span>
            </a>
            <a href="mailto:info@audioapex.co.uk" className="flex items-center gap-2 text-white hover:text-[#e2e8f0] transition-colors" data-testid="contact-hero-email">
              <div className="w-9 h-9 bg-[#e2e8f0] rounded-lg flex items-center justify-center shadow-md">
                <Mail className="w-4 h-4 text-slate-900" />
              </div>
              <span className="font-medium">info@audioapex.co.uk</span>
            </a>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Contact Form */}
          <div>
            <Card className="border-slate-700/50 bg-slate-800/50 backdrop-blur-sm shadow-lg text-white">
              <CardContent className="p-6">
                <div className="mb-5">
                  <h2 className="text-xl font-bold text-white mb-2 heading-font">Get Your Custom Quote</h2>
                  <p className="text-gray-400 text-sm">Fill in the details below and we'll get back to you with a tailored rental package.</p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1.5">
                        Full Name *
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Smith"
                        className="border-slate-600 bg-slate-700/50 text-white placeholder:text-gray-400 focus:border-[#e2e8f0] focus:ring-[#e2e8f0] h-10 rounded-lg text-sm"
                        data-testid="contact-name-input"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1.5">
                        Email Address *
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className="border-slate-600 bg-slate-700/50 text-white placeholder:text-gray-400 focus:border-[#e2e8f0] focus:ring-[#e2e8f0] h-10 rounded-lg text-sm"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-1.5">
                      Phone Number *
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+44 20 1234 5678"
                      className="border-slate-600 bg-slate-700/50 text-white placeholder:text-gray-400 focus:border-[#e2e8f0] focus:ring-[#e2e8f0] h-10 rounded-lg text-sm"
                      data-testid="contact-phone-input"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="venueCapacity" className="block text-sm font-medium text-gray-300 mb-1.5">
                        Venue Capacity *
                      </label>
                      <Input
                        id="venueCapacity"
                        name="venueCapacity"
                        type="text"
                        required
                        value={formData.venueCapacity}
                        onChange={handleChange}
                        placeholder="e.g. 500 people"
                        className="border-slate-600 bg-slate-700/50 text-white placeholder:text-gray-400 focus:border-[#e2e8f0] focus:ring-[#e2e8f0] h-10 rounded-lg text-sm"
                        data-testid="contact-capacity-input"
                      />
                    </div>

                    <div>
                      <label htmlFor="eventDate" className="block text-sm font-medium text-gray-300 mb-1.5">
                        Event Date *
                      </label>
                      <Input
                        id="eventDate"
                        name="eventDate"
                        type="date"
                        required
                        value={formData.eventDate}
                        onChange={handleChange}
                        className="border-slate-600 bg-slate-700/50 text-white placeholder:text-gray-400 focus:border-[#e2e8f0] focus:ring-[#e2e8f0] h-10 rounded-lg text-sm"
                        data-testid="contact-date-input"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="eventType" className="block text-sm font-medium text-gray-300 mb-1.5">
                        Event Type *
                      </label>
                      <select
                        id="eventType"
                        name="eventType"
                        required
                        value={formData.eventType}
                        onChange={handleChange}
                        className="w-full border border-slate-600 bg-slate-700/50 text-white placeholder:text-gray-400 focus:border-[#e2e8f0] focus:ring-[#e2e8f0] h-10 rounded-lg text-sm px-3 outline-none"
                        data-testid="contact-event-type-select"
                      >
                        <option value="" disabled>Select event type</option>
                        <option value="Brand Activation">Brand Activation</option>
                        <option value="Corporate or Conference">Corporate or Conference</option>
                        <option value="Gallery or Exhibition">Gallery or Exhibition</option>
                        <option value="Other">Other</option>
                        <option value="Private Party">Private Party</option>
                        <option value="Wedding">Wedding</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="rentalDuration" className="block text-sm font-medium text-gray-300 mb-1.5">
                        Rental Duration *
                      </label>
                      <Input
                        id="rentalDuration"
                        name="rentalDuration"
                        type="text"
                        required
                        value={formData.rentalDuration}
                        onChange={handleChange}
                        placeholder="e.g. 1 day, 1 week, 2 months"
                        className="border-slate-600 bg-slate-700/50 text-white placeholder:text-gray-400 focus:border-[#e2e8f0] focus:ring-[#e2e8f0] h-10 rounded-lg text-sm"
                        data-testid="contact-duration-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="venueAccessNotes" className="block text-sm font-medium text-gray-300 mb-1.5">
                      Venue Access Notes (optional)
                    </label>
                    <Input
                      id="venueAccessNotes"
                      name="venueAccessNotes"
                      type="text"
                      value={formData.venueAccessNotes}
                      onChange={handleChange}
                      placeholder="e.g. ground floor, lift available, stairs only, parking on site"
                      className="border-slate-600 bg-slate-700/50 text-white placeholder:text-gray-400 focus:border-[#e2e8f0] focus:ring-[#e2e8f0] h-10 rounded-lg text-sm"
                      data-testid="contact-venue-access-input"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-1.5">
                      Additional Requirements (optional)
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your event, the kind of atmosphere you want, and any setup or timing details."
                      rows={4}
                      className="border-slate-600 bg-slate-700/50 text-white placeholder:text-gray-400 focus:border-[#e2e8f0] focus:ring-[#e2e8f0] resize-none rounded-lg text-sm"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full bg-[#e2e8f0] hover:bg-[#d1d5db] text-slate-900 transition-all duration-300 h-11 text-sm font-medium disabled:opacity-70"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Request Quote
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Information */}
          <div className="space-y-5">
            <div>
              <h2 className="text-xl font-bold text-white mb-2 heading-font">Why Choose Audio Apex?</h2>
              <p className="text-gray-400 leading-relaxed text-sm mb-5">
                Premier PA system and equipment rental with flexible terms, cutting-edge gear, 
                and 24/7 support for events from 20-2,000 guests across London and surrounding areas.
              </p>

              {/* Key Benefits */}
              <div className="space-y-2 mb-5">
                {[
                  { icon: Zap, text: 'Premium HiFi sound and design' },
                  { icon: Users, text: 'Events from 20-2,000 Guests' },
                  { icon: Calendar, text: 'Last Minute & Long Term Rentals' },
                  { icon: Clock, text: '24/7 Emergency Hire Available' }
                ].map((benefit, idx) => (
                  <div key={benefit.text} className="flex items-center gap-3 bg-slate-800/50 backdrop-blur-sm p-3 rounded-lg border border-slate-700/50 shadow-sm">
                    <div className="w-9 h-9 bg-[#e2e8f0] rounded-lg flex items-center justify-center flex-shrink-0 shadow-md">
                      <benefit.icon className="w-4 h-4 text-slate-900" />
                    </div>
                    <span className="font-medium text-gray-300 text-sm">{benefit.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              {contactInfo.map((info, idx) => (
                <Card key={idx} className="border-slate-700/50 bg-slate-800/50 hover:shadow-lg transition-all duration-300 backdrop-blur-sm shadow-sm text-white">
                  <CardContent className="p-4">
                    <div className="w-9 h-9 bg-[#e2e8f0] rounded-lg flex items-center justify-center mb-2 shadow-md">
                      <info.icon className="w-4 h-4 text-slate-900" />
                    </div>
                    <h3 className="text-sm font-bold text-white mb-1">{info.title}</h3>
                    {info.details.map((detail, detailIdx) => (
                      <p key={detailIdx} className="text-gray-400 text-xs">
                        {detail}
                      </p>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Location Card */}
            <Card className="border-slate-700/50 bg-slate-800/50 overflow-hidden">
              <div className="relative h-48">
                <img
                  src="https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=800"
                  alt="London Service Area"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent flex items-end p-5">
                  <div>
                    <h3 className="text-white font-bold text-lg mb-0.5">Serving London & Beyond</h3>
                    <p className="text-gray-300 text-sm">Fast delivery across London and surrounding areas</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
